import { MutableRefObject, useEffect, useRef, useState } from 'react';
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import { trackDocumentViewerOpened, type DocumentViewerTracking } from './observability/prometheus';
import { APRYSE_KEY } from 'shared/constants';

export type { DocumentViewerTracking };

interface DocumentViewerProps {
  documentTobeLoaded: File | { name?: string } | null | undefined;
  tracking?: DocumentViewerTracking;
}

/**
 * Component used to display and interact with a PDF document using WebViewer.
 * @component
 * @param {DocumentViewerProps} props - The props of the DocumentViewer component.
 * @returns {JSX.Element} The DocumentViewer component.
 */
const DocumentViewer = ({ documentTobeLoaded, tracking }: DocumentViewerProps) => {
  const viewer: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const [instance, setInstance] = useState<WebViewerInstance>();
  const trackedDocumentRef = useRef<string | null>(null);
  const documentRef = useRef(documentTobeLoaded);
  const trackingRef = useRef(tracking);

  documentRef.current = documentTobeLoaded;
  trackingRef.current = tracking;

  const trackDocumentOpen = (): void => {
    const document = documentRef.current;
    const currentTracking = trackingRef.current;
    const documentName = document?.name || 'unknown';
    const documentKey = `${documentName}|${currentTracking?.documentId || 'unknown'}`;

    if (trackedDocumentRef.current === documentKey) {
      return;
    }

    trackedDocumentRef.current = documentKey;
    trackDocumentViewerOpened({
      documentName,
      documentId: currentTracking?.documentId,
      sourcePage: currentTracking?.sourcePage,
      viewerContext: currentTracking?.viewerContext
    });
  };

  useEffect(() => {
    trackedDocumentRef.current = null;
  }, [documentTobeLoaded, tracking?.documentId, tracking?.sourcePage, tracking?.viewerContext]);

  useEffect(() => {
    if (!instance) {
      if (!viewer.current) {
        return;
      }

      WebViewer(
        {
          path: '/webviewer/lib',
          licenseKey: APRYSE_KEY
        },
        viewer.current
      ).then((inst) => {
        inst.Core.disableEmbeddedJavaScript();
        setInstance(inst);
        const { documentViewer, annotationManager } = inst.Core;
        documentViewer.addEventListener('documentLoaded', () => {
          annotationManager.enableReadOnlyMode();
          trackDocumentOpen();
        });
      });
      return;
    }

    if (!documentTobeLoaded) {
      return;
    }

    instance.UI.loadDocument(documentTobeLoaded as File, { filename: documentTobeLoaded?.name });
  }, [documentTobeLoaded, instance]);

  return <section className={'h-full'} ref={viewer}></section>;
};

export default DocumentViewer;
